import { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, CalendarDays, RefreshCw, Zap } from "lucide-react";
import { api } from "../lib/api";
import { useSeo, breadcrumbJsonLd, articleJsonLd, faqJsonLd } from "../lib/seo";
import { articleUrl, categoryUrl, formatDate, CONTENT_TYPE_LABEL } from "../lib/format";
import { ArticleCard, ArticleGrid } from "../components/articles/ArticleCard";
import { Breadcrumbs } from "../components/common/Breadcrumbs";
import { Newsletter } from "../components/common/Newsletter";
import { AdSlot } from "../components/common/AdSlot";
import { PageLoader } from "../components/common/States";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import NotFound from "./NotFound";

function splitIntro(html) {
  const idx = html.indexOf("<h2");
  return idx > 0 ? [html.slice(0, idx), html.slice(idx)] : [html, ""];
}

export default function ArticlePage({ preview = false }) {
  const { categorySlug, slug, id } = useParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    const req = preview ? api.get(`/admin/preview/${id}`) : api.get(`/articles/${slug}`);
    req.then((r) => { setData(r.data); setStatus("ok"); }).catch((e) => setStatus(e?.response?.status === 404 ? "404" : "error"));
  }, [slug, id, preview, location.key]);

  const a = data?.article;
  const crumbs = a ? [{ name: "Home", path: "/" }, { name: a.category_name, path: categoryUrl(a.category_slug) }, { name: a.title, path: articleUrl(a) }] : [];
  useSeo({
    title: a?.seo_title || a?.title, description: a?.seo_description || a?.excerpt, path: a ? articleUrl(a) : undefined, image: a?.featured_image, type: "article",
    canonical: a?.canonical_url || undefined, robots: preview ? "noindex,nofollow" : "index,follow",
    jsonLd: a ? [articleJsonLd(a, data.author), breadcrumbJsonLd(crumbs), faqJsonLd(a.faqs)] : [],
  });

  if (status === "404") return <NotFound />;
  if (status !== "ok") return <PageLoader />;
  if (!preview && a.category_slug !== categorySlug) return <NotFound />;

  const [intro, body] = splitIntro(a.content || "");
  const updated = a.updated_at && a.published_at && a.updated_at.slice(0, 10) !== a.published_at.slice(0, 10);

  return (
    <article data-testid="article-page">
      {preview && <div className="bg-amber-100 py-2 text-center text-sm font-semibold text-amber-900" data-testid="preview-banner">Preview mode · status: {a.status}</div>}
      <header className="container-ab max-w-4xl pt-10 lg:pt-16">
        <Breadcrumbs items={crumbs} />
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link to={categoryUrl(a.category_slug)} className="eyebrow" data-testid="article-category-link">{a.category_name}</Link>
          <span className="rounded-full bg-ab-soft px-2.5 py-0.5 text-[11px] font-semibold text-ab-muted">{CONTENT_TYPE_LABEL[a.content_type] || "Article"}</span>
        </div>
        <h1 className="mt-4 font-serif text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl" data-testid="article-title">{a.title}</h1>
        <p className="mt-6 max-w-3xl text-xl leading-relaxed text-ab-text-2" data-testid="article-excerpt">{a.excerpt}</p>
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 border-y border-ab-border py-4 text-sm text-ab-muted" data-testid="article-meta">
          <Link to={`/author/${a.author_slug}/`} className="flex items-center gap-2 font-semibold text-ab-text hover:text-ab-accent" data-testid="article-author-link">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ab-accent text-xs font-bold text-white">AB</span>{a.author_name}
          </Link>
          <span className="flex items-center gap-1.5"><CalendarDays size={14} /> Published <time dateTime={a.published_at}>{formatDate(a.published_at)}</time></span>
          {updated && <span className="flex items-center gap-1.5"><RefreshCw size={14} /> Updated <time dateTime={a.updated_at}>{formatDate(a.updated_at)}</time></span>}
          <span className="flex items-center gap-1.5"><Clock size={14} /> {a.reading_time} min read</span>
        </div>
      </header>

      {a.featured_image && (
        <figure className="container-ab mt-10 max-w-5xl">
          <img src={a.featured_image} alt={a.image_alt || a.title} className="aspect-[16/9] w-full rounded-3xl object-cover" fetchPriority="high" data-testid="article-hero-image" />
          {a.image_alt && <figcaption className="mt-3 text-center text-xs text-ab-subtle">{a.image_alt}</figcaption>}
        </figure>
      )}

      <div className="container-ab mt-12 grid gap-14 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-8 lg:col-start-1">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
            {a.quick_answer && (
              <aside className="mb-10 rounded-2xl border border-primary/30 bg-ab-accent-soft p-6 sm:p-8" aria-labelledby="quick-answer-heading" data-testid="quick-answer-box">
                <p id="quick-answer-heading" className="eyebrow flex items-center gap-2"><Zap size={14} /> Quick Answer</p>
                <p className="mt-3 font-serif text-xl leading-snug text-[#1D5239] sm:text-2xl">{a.quick_answer}</p>
              </aside>
            )}
            <div className="prose-ab" dangerouslySetInnerHTML={{ __html: intro }} data-testid="article-intro" />
            <AdSlot slot="article-after-intro" size="inline" />
            <div className="prose-ab" dangerouslySetInnerHTML={{ __html: body }} data-testid="article-body" />
            <AdSlot slot="article-after-content" size="inline" />

            {a.tags?.length > 0 && (
              <ul className="mt-12 flex flex-wrap gap-2" aria-label="Tags" data-testid="article-tags">
                {a.tags.map((t) => <li key={t}><Link to={`/search/?q=${encodeURIComponent(t.replace(/-/g, " "))}`} className="inline-flex rounded-full bg-ab-soft px-3.5 py-1.5 text-xs font-semibold text-ab-text-2 hover:bg-ab-accent-soft hover:text-ab-accent-hover">#{t}</Link></li>)}
              </ul>
            )}

            {a.faqs?.length > 0 && (
              <section className="mt-16" aria-labelledby="faq-heading" data-testid="article-faq">
                <h2 id="faq-heading" className="font-serif text-3xl font-semibold">Frequently Asked Questions</h2>
                <Accordion type="single" collapsible className="mt-6">
                  {a.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-ab-border">
                      <AccordionTrigger className="text-left font-serif text-lg font-semibold hover:no-underline" data-testid={`faq-trigger-${i}`}>{f.question}</AccordionTrigger>
                      <AccordionContent className="text-base leading-relaxed text-ab-text-2">{f.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            )}

            <div className="mt-14 rounded-2xl border border-ab-border bg-ab-warm p-6 text-sm text-ab-muted" data-testid="article-trust-box">
              Written and reviewed by the <Link to={`/author/${a.author_slug}/`} className="font-semibold text-ab-text underline-offset-2 hover:underline">{a.author_name}</Link>. Articles are researched, reviewed and updated when products or habits change. Read our <Link to="/editorial-policy/" className="font-semibold text-ab-accent hover:underline">editorial policy</Link> and <Link to="/affiliate-disclosure/" className="font-semibold text-ab-accent hover:underline">disclosures</Link>.
            </div>
          </div>
        </div>

        <aside className="space-y-8 lg:col-span-4" aria-label="Sidebar">
          <div className="lg:sticky lg:top-24 lg:space-y-8">
            {data.popular?.length > 0 && (
              <section className="card-ab p-6" data-testid="article-popular">
                <p className="eyebrow">Popular right now</p>
                <div className="mt-4 space-y-5">{data.popular.slice(0, 4).map((p) => <ArticleCard key={p.id} article={p} variant="compact" />)}</div>
              </section>
            )}
            <AdSlot slot="article-rail" size="rectangle" className="!my-0" />
            <Newsletter variant="inline" source="article" />
          </div>
        </aside>
      </div>

      {(data.prev || data.next) && (
        <nav className="container-ab mt-20 grid gap-4 sm:grid-cols-2" aria-label="Previous and next articles" data-testid="article-prev-next">
          {data.prev ? <Link to={articleUrl(data.prev)} className="card-ab group p-6 transition-colors hover:border-ab-accent" data-testid="prev-article-link"><span className="flex items-center gap-1 text-xs text-ab-muted"><ArrowLeft size={12} /> Previous</span><span className="mt-2 block font-serif text-lg font-semibold group-hover:text-ab-accent">{data.prev.title}</span></Link> : <span />}
          {data.next && <Link to={articleUrl(data.next)} className="card-ab group p-6 text-right transition-colors hover:border-ab-accent" data-testid="next-article-link"><span className="flex items-center justify-end gap-1 text-xs text-ab-muted">Next <ArrowRight size={12} /></span><span className="mt-2 block font-serif text-lg font-semibold group-hover:text-ab-accent">{data.next.title}</span></Link>}
        </nav>
      )}

      {data.related?.length > 0 && (
        <section className="container-ab mt-20" aria-labelledby="related-heading" data-testid="related-articles">
          <h2 id="related-heading" className="font-serif text-3xl font-semibold">Related Articles</h2>
          <div className="mt-8"><ArticleGrid articles={data.related} cols={4} /></div>
        </section>
      )}
    </article>
  );
}
